import type { GuideLanguageCode, GuideTranslatedLanguageCode } from "@/lib/guides/languages";

export type GuideUiStrings = {
  locale: string;
  guide: string;
  trip: string;
  guides: string;
  travelGuide: string;
  loadingTrip: string;
  save: string;
  saved: string;
  saveGuideToProfile: string;
  removeSavedTrips: string;
  share: string;
  shareGuide: string;
  shareThisGuide: string;
  back: string;
  edit: string;
  draft: string;
  handcrafted: string;
  aiEnhanced: string;
  curatedBy: string;
  generatedBy: string;
  draftNotice: string;
  guideSections: string;
  emailGuide: string;
  watch: string;
  openInstagram: string;
  openTikTok: string;
  openYouTube: string;
  openThreads: string;
  teraviaCurator: string;
  badgeCurator: string;
  badgePioneer: string;
  badgeVerifiedOperator: string;
  tabAll: string;
  tabIntro: string;
  tabBestFor: string;
  tabWhyVisit: string;
  tabHowToGetThere: string;
  tabPrepared: string;
  tabEssentials: string;
  tabSeasonal: string;
  tabEvents: string;
  tabActivities: string;
  tabWhatToDo: string;
  tabFoodScene: string;
  tabSustainability: string;
  tabGettingAround: string;
  tabPractical: string;
  tabSafety: string;
  tabPosters: string;
  tabMistakes: string;
  tabItinerary: string;
  tabFaq: string;
  tabPlaces: string;
  crowdPatterns: string;
  weatherToExpect: string;
  events: string;
  foodScene: string;
  dishesToTry: string;
  drinksToTry: string;
  whereToTryThem: string;
  sustainability: string;
  travelLighterHere: string;
  betterLocalChoices: string;
  whereToDoIt: string;
  notes: string;
  gettingAround: string;
  practicalInformation: string;
  healthAndSafety: string;
  healthSafetyDisclaimer: string;
  commonMistakesToAvoid: string;
  mistake: string;
  instead: string;
  sources: string;
  source: string;
  localSource: string;
  showRoute: string;
  hideRoute: string;
  showOnMaps: string;
  bookingLink: string;
  nearest: string;
  fromPrefix: string;
  openMap: string;
  link: string;
  mapFood: string;
  mapSustainability: string;
  modePlane: string;
  modeTrain: string;
  modeCar: string;
  modeFerry: string;
  modeBus: string;
  modeOther: string;
  eventFestival: string;
  eventConcert: string;
  eventFood: string;
  eventSports: string;
  eventMarket: string;
  eventCulture: string;
  eventFamily: string;
  eventSeasonal: string;
  eventDefault: string;
  sustainabilityTransport: string;
  sustainabilityEv: string;
  sustainabilityFood: string;
  sustainabilityWater: string;
  sustainabilityWaste: string;
  sustainabilityNature: string;
  sustainabilityClimate: string;
  sustainabilityStay: string;
  healthTypeEmergency: string;
  healthTypeHealthcare: string;
  healthTypeNature: string;
  healthTypeWeather: string;
  healthTypeWater: string;
  healthTypeCrime: string;
  healthTypeTransport: string;
  healthTypeVaccines: string;
  healthTypeOther: string;
  rain: string;
  sun: string;
  continueLabel: string;
  continueGuideTitle: string;
  continueGuideBody: string;
  saveAsIs: string;
  makeItYours: string;
  sharedToast: string;
  linkCopiedToast: string;
  couldntShareToast: string;
  foodPosterAlt: string;
  getPreparedTitle: string;
  getPreparedSubtitle: string;
  getPreparedEmpty: string;
  preparedClothes: string;
  preparedManners: string;
  preparedVocabulary: string;
  preparedBooks: string;
  preparedMusic: string;
  preparedCinema: string;
  preparedCategory: string;
  preparedBring: string;
  preparedSituation: string;
  guideNotFoundTitle: string;
  guideNotFoundDescription: string;
};

const ENGLISH_UI: GuideUiStrings = {
  locale: "en",
  guide: "Guide",
  trip: "Trip",
  guides: "Guides",
  travelGuide: "Travel guide",
  loadingTrip: "Loading trip…",
  save: "Save",
  saved: "Saved",
  saveGuideToProfile: "Save this guide to your profile",
  removeSavedTrips: "Remove from your saved trips",
  share: "Share",
  shareGuide: "Share guide",
  shareThisGuide: "Share this guide",
  back: "Back",
  edit: "Edit",
  draft: "Draft",
  handcrafted: "Handcrafted",
  aiEnhanced: "AI-enhanced",
  curatedBy: "Curated by",
  generatedBy: "Generated by",
  draftNotice: "Draft - publish when ready to make this public.",
  guideSections: "Guide sections",
  emailGuide: "Email this guide",
  watch: "Watch",
  openInstagram: "Open Instagram",
  openTikTok: "Open TikTok",
  openYouTube: "Open YouTube",
  openThreads: "Open Threads",
  teraviaCurator: "Teravia curator",
  badgeCurator: "Curator",
  badgePioneer: "Pioneer",
  badgeVerifiedOperator: "Verified operator",
  tabAll: "All",
  tabIntro: "Intro",
  tabBestFor: "Best for",
  tabWhyVisit: "Why visit",
  tabHowToGetThere: "How to get there",
  tabPrepared: "Prepared",
  tabEssentials: "Essentials",
  tabSeasonal: "Seasonal",
  tabEvents: "Events",
  tabActivities: "Activities",
  tabWhatToDo: "What to do",
  tabFoodScene: "Food scene",
  tabSustainability: "Sustainability",
  tabGettingAround: "Getting around",
  tabPractical: "Practical",
  tabSafety: "Safety",
  tabPosters: "Posters",
  tabMistakes: "Mistakes",
  tabItinerary: "Itinerary",
  tabFaq: "FAQ",
  tabPlaces: "Places",
  crowdPatterns: "Crowd patterns",
  weatherToExpect: "Weather to expect",
  events: "Events",
  foodScene: "Food scene",
  dishesToTry: "Dishes to try",
  drinksToTry: "Wines, beers and spirits to try",
  whereToTryThem: "Where to try them",
  sustainability: "Sustainability",
  travelLighterHere: "Travel lighter here",
  betterLocalChoices: "Better local choices",
  whereToDoIt: "Where to do it",
  notes: "Notes",
  gettingAround: "Getting around",
  practicalInformation: "Practical information",
  healthAndSafety: "Health & safety",
  healthSafetyDisclaimer: "General guidance only. Always check official health and safety advisories for your situation.",
  commonMistakesToAvoid: "Common mistakes to avoid",
  mistake: "Mistake",
  instead: "Instead",
  sources: "Sources",
  source: "Source",
  localSource: "Local source",
  showRoute: "Show route",
  hideRoute: "Hide route",
  showOnMaps: "Show on Maps",
  bookingLink: "Booking link",
  nearest: "Nearest",
  fromPrefix: "From",
  openMap: "Open map",
  link: "Link",
  mapFood: "Food map",
  mapSustainability: "Sustainability map",
  modePlane: "Plane",
  modeTrain: "Train",
  modeCar: "Car",
  modeFerry: "Ferry",
  modeBus: "Bus",
  modeOther: "Other",
  eventFestival: "Festival",
  eventConcert: "Concert",
  eventFood: "Food",
  eventSports: "Sports",
  eventMarket: "Market",
  eventCulture: "Culture",
  eventFamily: "Family",
  eventSeasonal: "Seasonal",
  eventDefault: "Event",
  sustainabilityTransport: "Transport",
  sustainabilityEv: "EV & rentals",
  sustainabilityFood: "Food",
  sustainabilityWater: "Water",
  sustainabilityWaste: "Waste",
  sustainabilityNature: "Nature",
  sustainabilityClimate: "Climate",
  sustainabilityStay: "Stay",
  healthTypeEmergency: "Emergency",
  healthTypeHealthcare: "Healthcare",
  healthTypeNature: "Nature",
  healthTypeWeather: "Weather",
  healthTypeWater: "Water",
  healthTypeCrime: "Crime",
  healthTypeTransport: "Transport",
  healthTypeVaccines: "Vaccines",
  healthTypeOther: "Other",
  rain: "Rain",
  sun: "Sun",
  continueLabel: "Continue",
  continueGuideTitle: "Continue this guide in teravia",
  continueGuideBody: "We'll prefill the destination. You shape it to your preferences.",
  saveAsIs: "Save as-is",
  makeItYours: "Make it yours",
  sharedToast: "Shared ✓",
  linkCopiedToast: "Link copied ✓",
  couldntShareToast: "Couldn't share link",
  foodPosterAlt: "Food poster",
  getPreparedTitle: "Get prepared",
  getPreparedSubtitle: "Before you go - a quick cultural and practical layer.",
  getPreparedEmpty: "Add content for this subsection (or use Enhance).",
  preparedClothes: "Clothes",
  preparedManners: "Manners",
  preparedVocabulary: "Vocabulary",
  preparedBooks: "Books",
  preparedMusic: "Music",
  preparedCinema: "Cinema",
  preparedCategory: "Category",
  preparedBring: "Bring",
  preparedSituation: "Situation",
  guideNotFoundTitle: "Guide not found · teravia",
  guideNotFoundDescription: "This guide is no longer available.",
};

const GUIDE_UI_BY_LANG: Record<GuideTranslatedLanguageCode, Partial<GuideUiStrings>> = {
  da: {
    locale: "da-DK",
    guide: "Guide",
    trip: "Tur",
    guides: "Guides",
    travelGuide: "Rejseguide",
    loadingTrip: "Indlaeser tur…",
    save: "Gem",
    saved: "Gemt",
    saveGuideToProfile: "Gem denne guide i din profil",
    removeSavedTrips: "Fjern fra dine gemte ture",
    share: "Del",
    shareGuide: "Del guide",
    shareThisGuide: "Del denne guide",
    back: "Tilbage",
    edit: "Rediger",
    draft: "Kladde",
    handcrafted: "Handlavet",
    aiEnhanced: "AI-forbedret",
    curatedBy: "Kurateret af",
    generatedBy: "Genereret af",
    draftNotice: "Kladde - udgiv den, nar du er klar til at gore den offentlig.",
    guideSections: "Guideafsnit",
    emailGuide: "Mail denne guide",
    watch: "Se",
    openInstagram: "Aben Instagram",
    openTikTok: "Aben TikTok",
    openYouTube: "Aben YouTube",
    openThreads: "Aben Threads",
    teraviaCurator: "Teravia-kurator",
    badgeCurator: "Kurator",
    badgePioneer: "Pioneer",
    badgeVerifiedOperator: "Verificeret operator",
    tabAll: "Alle",
    tabIntro: "Intro",
    tabBestFor: "Bedst til",
    tabWhyVisit: "Hvorfor tage dertil",
    tabHowToGetThere: "Saadan kommer du dertil",
    tabPrepared: "Forbered",
    tabEssentials: "Det vigtigste",
    tabSeasonal: "Saeson",
    tabEvents: "Begivenheder",
    tabActivities: "Aktiviteter",
    tabWhatToDo: "Hvad skal man lave",
    tabFoodScene: "Madscene",
    tabSustainability: "Baeredygtighed",
    tabGettingAround: "Transport lokalt",
    tabPractical: "Praktisk",
    tabSafety: "Sikkerhed",
    tabPosters: "Plakater",
    tabMistakes: "Fejl",
    tabItinerary: "Rejseplan",
    tabFaq: "FAQ",
    tabPlaces: "Steder",
    crowdPatterns: "Traengselsniveau",
    weatherToExpect: "Forventet vejr",
    events: "Begivenheder",
    foodScene: "Madscene",
    dishesToTry: "Retter du boer prove",
    drinksToTry: "Vine, ol og spiritus du boer prove",
    whereToTryThem: "Hvor du kan prove dem",
    sustainability: "Baeredygtighed",
    travelLighterHere: "Rejs lettere her",
    betterLocalChoices: "Bedre lokale valg",
    whereToDoIt: "Hvor du kan gore det",
    notes: "Noter",
    gettingAround: "Transport lokalt",
    practicalInformation: "Praktisk information",
    healthAndSafety: "Sundhed og sikkerhed",
    healthSafetyDisclaimer: "Kun generel vejledning. Tjek altid officielle sundheds- og sikkerhedsanbefalinger for din situation.",
    commonMistakesToAvoid: "Typiske fejl at undga",
    mistake: "Fejl",
    instead: "I stedet",
    sources: "Kilder",
    source: "Kilde",
    localSource: "Lokal kilde",
    showRoute: "Vis rute",
    hideRoute: "Skjul rute",
    showOnMaps: "Vis pa kort",
    bookingLink: "Bookinglink",
    nearest: "Naermest",
    fromPrefix: "Fra",
    openMap: "Aben kort",
    link: "Link",
    mapFood: "Madkort",
    mapSustainability: "Baeredygtighedskort",
    modePlane: "Fly",
    modeTrain: "Tog",
    modeCar: "Bil",
    modeFerry: "Faerge",
    modeBus: "Bus",
    modeOther: "Andet",
    eventFestival: "Festival",
    eventConcert: "Koncert",
    eventFood: "Mad",
    eventSports: "Sport",
    eventMarket: "Marked",
    eventCulture: "Kultur",
    eventFamily: "Familie",
    eventSeasonal: "Saeson",
    eventDefault: "Begivenhed",
    sustainabilityTransport: "Transport",
    sustainabilityEv: "Elbiler og udlejning",
    sustainabilityFood: "Mad",
    sustainabilityWater: "Vand",
    sustainabilityWaste: "Affald",
    sustainabilityNature: "Natur",
    sustainabilityClimate: "Klima",
    sustainabilityStay: "Ophold",
    healthTypeEmergency: "Noedtilfaelde",
    healthTypeHealthcare: "Sundhedsvaesen",
    healthTypeNature: "Natur",
    healthTypeWeather: "Vejr",
    healthTypeWater: "Vand",
    healthTypeCrime: "Kriminalitet",
    healthTypeTransport: "Transport",
    healthTypeVaccines: "Vacciner",
    healthTypeOther: "Andet",
    rain: "Regn",
    sun: "Sol",
    continueLabel: "Fortsat",
    continueGuideTitle: "Fortsat denne guide i teravia",
    continueGuideBody: "Vi udfylder destinationen pa forhand. Du former den efter dine praeferencer.",
    saveAsIs: "Gem som den er",
    makeItYours: "Gor den til din",
    sharedToast: "Delt ✓",
    linkCopiedToast: "Link kopieret ✓",
    couldntShareToast: "Kunne ikke dele linket",
    foodPosterAlt: "Madplakat",
    getPreparedTitle: "Forbered dig",
    getPreparedSubtitle: "Foer du tager afsted - et hurtigt kulturelt og praktisk lag.",
    getPreparedEmpty: "Tilfoj indhold til dette underafsnit (eller brug Enhance).",
    preparedClothes: "Toj",
    preparedManners: "Manerer",
    preparedVocabulary: "Ordforrad",
    preparedBooks: "Boeger",
    preparedMusic: "Musik",
    preparedCinema: "Film",
    preparedCategory: "Kategori",
    preparedBring: "Tag med",
    preparedSituation: "Situation",
    guideNotFoundTitle: "Guide ikke fundet · teravia",
    guideNotFoundDescription: "Denne guide er ikke laengere tilgaengelig.",
  },
  fr: {
    locale: "fr-FR",
    guide: "Guide",
    trip: "Voyage",
    guides: "Guides",
    travelGuide: "Guide de voyage",
    loadingTrip: "Chargement du voyage…",
    save: "Enregistrer",
    saved: "Enregistre",
    saveGuideToProfile: "Enregistrer ce guide dans votre profil",
    removeSavedTrips: "Retirer de vos voyages enregistres",
    share: "Partager",
    shareGuide: "Partager le guide",
    shareThisGuide: "Partager ce guide",
    back: "Retour",
    edit: "Modifier",
    draft: "Brouillon",
    handcrafted: "Artisanal",
    aiEnhanced: "Ameliore par l'IA",
    curatedBy: "Cure par",
    generatedBy: "Genere par",
    draftNotice: "Brouillon - publiez-le quand vous etes pret a le rendre public.",
    guideSections: "Sections du guide",
    emailGuide: "Envoyer ce guide par e-mail",
    watch: "Voir",
    openInstagram: "Ouvrir Instagram",
    openTikTok: "Ouvrir TikTok",
    openYouTube: "Ouvrir YouTube",
    openThreads: "Ouvrir Threads",
    teraviaCurator: "Curateur Teravia",
    badgeCurator: "Curateur",
    badgePioneer: "Pionnier",
    badgeVerifiedOperator: "Operateur verifie",
    tabAll: "Tout",
    tabIntro: "Intro",
    tabBestFor: "Ideal pour",
    tabWhyVisit: "Pourquoi y aller",
    tabHowToGetThere: "Comment y aller",
    tabPrepared: "Preparation",
    tabEssentials: "Essentiel",
    tabSeasonal: "Saisons",
    tabEvents: "Evenements",
    tabActivities: "Activités",
    tabWhatToDo: "Que faire",
    tabFoodScene: "Scene culinaire",
    tabSustainability: "Durabilite",
    tabGettingAround: "Se deplacer",
    tabPractical: "Pratique",
    tabSafety: "Securite",
    tabPosters: "Posters",
    tabMistakes: "Erreurs",
    tabItinerary: "Itineraire",
    tabFaq: "FAQ",
    tabPlaces: "Lieux",
    crowdPatterns: "Affluence",
    weatherToExpect: "Meteo a prevoir",
    events: "Evenements",
    foodScene: "Scene culinaire",
    dishesToTry: "Plats a gouter",
    drinksToTry: "Vins, bieres et spiritueux a gouter",
    whereToTryThem: "Ou les gouter",
    sustainability: "Durabilite",
    travelLighterHere: "Voyager plus leger ici",
    betterLocalChoices: "Meilleurs choix locaux",
    whereToDoIt: "Ou le faire",
    notes: "Notes",
    gettingAround: "Se deplacer",
    practicalInformation: "Informations pratiques",
    healthAndSafety: "Sante et securite",
    healthSafetyDisclaimer: "Conseils generaux uniquement. Verifiez toujours les avis officiels de sante et de securite selon votre situation.",
    commonMistakesToAvoid: "Erreurs frequentes a eviter",
    mistake: "Erreur",
    instead: "A la place",
    sources: "Sources",
    source: "Source",
    localSource: "Source locale",
    showRoute: "Afficher l'itineraire",
    hideRoute: "Masquer l'itineraire",
    showOnMaps: "Voir sur Maps",
    bookingLink: "Lien de reservation",
    nearest: "Le plus proche",
    fromPrefix: "Depuis",
    openMap: "Ouvrir la carte",
    link: "Lien",
    mapFood: "Carte gourmande",
    mapSustainability: "Carte durabilite",
    modePlane: "Avion",
    modeTrain: "Train",
    modeCar: "Voiture",
    modeFerry: "Ferry",
    modeBus: "Bus",
    modeOther: "Autre",
    eventFestival: "Festival",
    eventConcert: "Concert",
    eventFood: "Gastronomie",
    eventSports: "Sport",
    eventMarket: "Marche",
    eventCulture: "Culture",
    eventFamily: "Famille",
    eventSeasonal: "Saisonnier",
    eventDefault: "Evenement",
    sustainabilityTransport: "Transport",
    sustainabilityEv: "VE et locations",
    sustainabilityFood: "Gastronomie",
    sustainabilityWater: "Eau",
    sustainabilityWaste: "Dechets",
    sustainabilityNature: "Nature",
    sustainabilityClimate: "Climat",
    sustainabilityStay: "Sejour",
    healthTypeEmergency: "Urgence",
    healthTypeHealthcare: "Soins",
    healthTypeNature: "Nature",
    healthTypeWeather: "Meteo",
    healthTypeWater: "Eau",
    healthTypeCrime: "Securite",
    healthTypeTransport: "Transport",
    healthTypeVaccines: "Vaccins",
    healthTypeOther: "Autre",
    rain: "Pluie",
    sun: "Soleil",
    continueLabel: "Continuer",
    continueGuideTitle: "Poursuivez ce guide dans teravia",
    continueGuideBody: "Nous pre-remplissons la destination. A vous de l'adapter a vos preferences.",
    saveAsIs: "Enregistrer tel quel",
    makeItYours: "Le personnaliser",
    sharedToast: "Partage ✓",
    linkCopiedToast: "Lien copie ✓",
    couldntShareToast: "Impossible de partager le lien",
    foodPosterAlt: "Poster culinaire",
    getPreparedTitle: "Bien se preparer",
    getPreparedSubtitle: "Avant de partir - une couche culturelle et pratique rapide.",
    getPreparedEmpty: "Ajoutez du contenu pour cette sous-section (ou utilisez Enhance).",
    preparedClothes: "Vetements",
    preparedManners: "Codes",
    preparedVocabulary: "Vocabulaire",
    preparedBooks: "Livres",
    preparedMusic: "Musique",
    preparedCinema: "Cinema",
    preparedCategory: "Categorie",
    preparedBring: "A emporter",
    preparedSituation: "Situation",
    guideNotFoundTitle: "Guide introuvable · teravia",
    guideNotFoundDescription: "Ce guide n'est plus disponible.",
  },
  de: {
    locale: "de-DE",
    guide: "Guide",
    trip: "Reise",
    guides: "Guides",
    travelGuide: "Reisefuehrer",
    loadingTrip: "Reise wird geladen…",
    save: "Speichern",
    saved: "Gespeichert",
    saveGuideToProfile: "Diesen Guide in deinem Profil speichern",
    removeSavedTrips: "Aus deinen gespeicherten Reisen entfernen",
    share: "Teilen",
    shareGuide: "Guide teilen",
    shareThisGuide: "Diesen Guide teilen",
    back: "Zurueck",
    edit: "Bearbeiten",
    draft: "Entwurf",
    handcrafted: "Handgemacht",
    aiEnhanced: "KI-veredelt",
    curatedBy: "Kuratiert von",
    generatedBy: "Erstellt von",
    draftNotice: "Entwurf - veroeffentliche ihn, wenn er oeffentlich sein soll.",
    guideSections: "Guide-Abschnitte",
    emailGuide: "Diesen Guide per E-Mail senden",
    watch: "Ansehen",
    openInstagram: "Instagram oeffnen",
    openTikTok: "TikTok oeffnen",
    openYouTube: "YouTube oeffnen",
    openThreads: "Threads oeffnen",
    teraviaCurator: "Teravia-Kurator",
    badgeCurator: "Kurator",
    badgePioneer: "Pionier",
    badgeVerifiedOperator: "Verifizierter Anbieter",
    tabAll: "Alles",
    tabIntro: "Intro",
    tabBestFor: "Am besten fuer",
    tabWhyVisit: "Warum hinfahren",
    tabHowToGetThere: "Anreise",
    tabPrepared: "Vorbereiten",
    tabEssentials: "Essentials",
    tabSeasonal: "Saisonal",
    tabEvents: "Events",
    tabActivities: "Aktivitäten",
    tabWhatToDo: "Was tun",
    tabFoodScene: "Kulinarik",
    tabSustainability: "Nachhaltigkeit",
    tabGettingAround: "Fortbewegung",
    tabPractical: "Praktisch",
    tabSafety: "Sicherheit",
    tabPosters: "Poster",
    tabMistakes: "Fehler",
    tabItinerary: "Reiseplan",
    tabFaq: "FAQ",
    tabPlaces: "Orte",
    crowdPatterns: "Besucherandrang",
    weatherToExpect: "Zu erwartendes Wetter",
    events: "Events",
    foodScene: "Kulinarik",
    dishesToTry: "Gerichte zum Probieren",
    drinksToTry: "Weine, Biere und Spirituosen zum Probieren",
    whereToTryThem: "Wo du sie probieren kannst",
    sustainability: "Nachhaltigkeit",
    travelLighterHere: "Hier bewusster reisen",
    betterLocalChoices: "Bessere lokale Entscheidungen",
    whereToDoIt: "Wo das moeglich ist",
    notes: "Hinweise",
    gettingAround: "Fortbewegung",
    practicalInformation: "Praktische Informationen",
    healthAndSafety: "Gesundheit und Sicherheit",
    healthSafetyDisclaimer: "Nur allgemeine Hinweise. Pruefe immer die offiziellen Gesundheits- und Sicherheitshinweise fuer deine Situation.",
    commonMistakesToAvoid: "Hauefige Fehler vermeiden",
    mistake: "Fehler",
    instead: "Stattdessen",
    sources: "Quellen",
    source: "Quelle",
    localSource: "Lokale Quelle",
    showRoute: "Route anzeigen",
    hideRoute: "Route ausblenden",
    showOnMaps: "Auf Maps anzeigen",
    bookingLink: "Buchungslink",
    nearest: "Am naechsten",
    fromPrefix: "Von",
    openMap: "Karte oeffnen",
    link: "Link",
    mapFood: "Kulinarik-Karte",
    mapSustainability: "Nachhaltigkeitskarte",
    modePlane: "Flugzeug",
    modeTrain: "Zug",
    modeCar: "Auto",
    modeFerry: "Faehre",
    modeBus: "Bus",
    modeOther: "Andere",
    eventFestival: "Festival",
    eventConcert: "Konzert",
    eventFood: "Kulinarik",
    eventSports: "Sport",
    eventMarket: "Markt",
    eventCulture: "Kultur",
    eventFamily: "Familie",
    eventSeasonal: "Saisonal",
    eventDefault: "Event",
    sustainabilityTransport: "Transport",
    sustainabilityEv: "E-Autos und Vermietung",
    sustainabilityFood: "Kulinarik",
    sustainabilityWater: "Wasser",
    sustainabilityWaste: "Abfall",
    sustainabilityNature: "Natur",
    sustainabilityClimate: "Klima",
    sustainabilityStay: "Unterkunft",
    healthTypeEmergency: "Notfall",
    healthTypeHealthcare: "Medizin",
    healthTypeNature: "Natur",
    healthTypeWeather: "Wetter",
    healthTypeWater: "Wasser",
    healthTypeCrime: "Sicherheit",
    healthTypeTransport: "Transport",
    healthTypeVaccines: "Impfungen",
    healthTypeOther: "Sonstiges",
    rain: "Regen",
    sun: "Sonne",
    continueLabel: "Weiter",
    continueGuideTitle: "Diesen Guide in teravia weiterfuehren",
    continueGuideBody: "Wir fuellen das Reiseziel vor. Du passt alles an deine Vorlieben an.",
    saveAsIs: "So speichern",
    makeItYours: "Zu deinem machen",
    sharedToast: "Geteilt ✓",
    linkCopiedToast: "Link kopiert ✓",
    couldntShareToast: "Link konnte nicht geteilt werden",
    foodPosterAlt: "Food-Poster",
    getPreparedTitle: "Gut vorbereitet",
    getPreparedSubtitle: "Vor der Reise - eine kurze kulturelle und praktische Ebene.",
    getPreparedEmpty: "Fuege Inhalte fuer diesen Unterabschnitt hinzu (oder nutze Enhance).",
    preparedClothes: "Kleidung",
    preparedManners: "Umgangsformen",
    preparedVocabulary: "Wortschatz",
    preparedBooks: "Buecher",
    preparedMusic: "Musik",
    preparedCinema: "Film",
    preparedCategory: "Kategorie",
    preparedBring: "Mitnehmen",
    preparedSituation: "Situation",
    guideNotFoundTitle: "Guide nicht gefunden · teravia",
    guideNotFoundDescription: "Dieser Guide ist nicht mehr verfuegbar.",
  },
  it: {
    locale: "it-IT",
    guide: "Guida",
    trip: "Viaggio",
    guides: "Guide",
    travelGuide: "Guida di viaggio",
    loadingTrip: "Caricamento viaggio…",
    save: "Salva",
    saved: "Salvato",
    saveGuideToProfile: "Salva questa guida nel tuo profilo",
    removeSavedTrips: "Rimuovi dai viaggi salvati",
    share: "Condividi",
    shareGuide: "Condividi guida",
    shareThisGuide: "Condividi questa guida",
    back: "Indietro",
    edit: "Modifica",
    draft: "Bozza",
    handcrafted: "Handcrafted",
    aiEnhanced: "Migliorata con IA",
    curatedBy: "Curata da",
    generatedBy: "Generata da",
    draftNotice: "Bozza - pubblicala quando sei pronto a renderla pubblica.",
    guideSections: "Sezioni della guida",
    emailGuide: "Invia questa guida via email",
    watch: "Guarda",
    openInstagram: "Apri Instagram",
    openTikTok: "Apri TikTok",
    openYouTube: "Apri YouTube",
    openThreads: "Apri Threads",
    teraviaCurator: "Curatore Teravia",
    badgeCurator: "Curatore",
    badgePioneer: "Pioneer",
    badgeVerifiedOperator: "Operatore verificato",
    tabAll: "Tutto",
    tabIntro: "Intro",
    tabBestFor: "Ideale per",
    tabWhyVisit: "Perche andarci",
    tabHowToGetThere: "Come arrivare",
    tabPrepared: "Prepararsi",
    tabEssentials: "Essenziali",
    tabSeasonal: "Stagioni",
    tabEvents: "Eventi",
    tabActivities: "Attività",
    tabWhatToDo: "Cosa fare",
    tabFoodScene: "Scena gastronomica",
    tabSustainability: "Sostenibilita",
    tabGettingAround: "Muoversi",
    tabPractical: "Pratico",
    tabSafety: "Sicurezza",
    tabPosters: "Poster",
    tabMistakes: "Errori",
    tabItinerary: "Itinerario",
    tabFaq: "FAQ",
    tabPlaces: "Luoghi",
    crowdPatterns: "Affluenza",
    weatherToExpect: "Meteo da aspettarsi",
    events: "Eventi",
    foodScene: "Scena gastronomica",
    dishesToTry: "Piatti da provare",
    drinksToTry: "Vini, birre e distillati da provare",
    whereToTryThem: "Dove provarli",
    sustainability: "Sostenibilita",
    travelLighterHere: "Viaggia piu leggero qui",
    betterLocalChoices: "Scelte locali migliori",
    whereToDoIt: "Dove farlo",
    notes: "Note",
    gettingAround: "Muoversi",
    practicalInformation: "Informazioni pratiche",
    healthAndSafety: "Salute e sicurezza",
    healthSafetyDisclaimer: "Indicazioni generali soltanto. Controlla sempre gli avvisi ufficiali di salute e sicurezza per la tua situazione.",
    commonMistakesToAvoid: "Errori comuni da evitare",
    mistake: "Errore",
    instead: "Invece",
    sources: "Fonti",
    source: "Fonte",
    localSource: "Fonte locale",
    showRoute: "Mostra percorso",
    hideRoute: "Nascondi percorso",
    showOnMaps: "Mostra su Maps",
    bookingLink: "Link prenotazione",
    nearest: "Piu vicino",
    fromPrefix: "Da",
    openMap: "Apri mappa",
    link: "Link",
    mapFood: "Mappa gastronomica",
    mapSustainability: "Mappa sostenibilita",
    modePlane: "Aereo",
    modeTrain: "Treno",
    modeCar: "Auto",
    modeFerry: "Traghetto",
    modeBus: "Bus",
    modeOther: "Altro",
    eventFestival: "Festival",
    eventConcert: "Concerto",
    eventFood: "Cibo",
    eventSports: "Sport",
    eventMarket: "Mercato",
    eventCulture: "Cultura",
    eventFamily: "Famiglia",
    eventSeasonal: "Stagionale",
    eventDefault: "Evento",
    sustainabilityTransport: "Trasporti",
    sustainabilityEv: "EV e noleggi",
    sustainabilityFood: "Cibo",
    sustainabilityWater: "Acqua",
    sustainabilityWaste: "Rifiuti",
    sustainabilityNature: "Natura",
    sustainabilityClimate: "Clima",
    sustainabilityStay: "Soggiorno",
    healthTypeEmergency: "Emergenza",
    healthTypeHealthcare: "Sanita",
    healthTypeNature: "Natura",
    healthTypeWeather: "Meteo",
    healthTypeWater: "Acqua",
    healthTypeCrime: "Sicurezza",
    healthTypeTransport: "Trasporti",
    healthTypeVaccines: "Vaccini",
    healthTypeOther: "Altro",
    rain: "Pioggia",
    sun: "Sole",
    continueLabel: "Continua",
    continueGuideTitle: "Continua questa guida in teravia",
    continueGuideBody: "Precompiliamo la destinazione. Tu la modelli secondo le tue preferenze.",
    saveAsIs: "Salva cosi",
    makeItYours: "Personalizzala",
    sharedToast: "Condiviso ✓",
    linkCopiedToast: "Link copiato ✓",
    couldntShareToast: "Impossibile condividere il link",
    foodPosterAlt: "Poster food",
    getPreparedTitle: "Preparati",
    getPreparedSubtitle: "Prima di partire - un rapido livello culturale e pratico.",
    getPreparedEmpty: "Aggiungi contenuti per questa sottosezione (o usa Enhance).",
    preparedClothes: "Abbigliamento",
    preparedManners: "Buone maniere",
    preparedVocabulary: "Vocabolario",
    preparedBooks: "Libri",
    preparedMusic: "Musica",
    preparedCinema: "Cinema",
    preparedCategory: "Categoria",
    preparedBring: "Porta con te",
    preparedSituation: "Situazione",
    guideNotFoundTitle: "Guida non trovata · teravia",
    guideNotFoundDescription: "Questa guida non e piu disponibile.",
  },
  es: {
    locale: "es-ES",
    guide: "Guia",
    trip: "Viaje",
    guides: "Guias",
    travelGuide: "Guia de viaje",
    loadingTrip: "Cargando viaje…",
    save: "Guardar",
    saved: "Guardado",
    saveGuideToProfile: "Guardar esta guia en tu perfil",
    removeSavedTrips: "Quitar de tus viajes guardados",
    share: "Compartir",
    shareGuide: "Compartir guia",
    shareThisGuide: "Compartir esta guia",
    back: "Atras",
    edit: "Editar",
    draft: "Borrador",
    handcrafted: "Artesanal",
    aiEnhanced: "Mejorada con IA",
    curatedBy: "Curada por",
    generatedBy: "Generada por",
    draftNotice: "Borrador - publicala cuando estes listo para hacerla publica.",
    guideSections: "Secciones de la guia",
    emailGuide: "Enviar esta guia por correo",
    watch: "Ver",
    openInstagram: "Abrir Instagram",
    openTikTok: "Abrir TikTok",
    openYouTube: "Abrir YouTube",
    openThreads: "Abrir Threads",
    teraviaCurator: "Curador de Teravia",
    badgeCurator: "Curador",
    badgePioneer: "Pionero",
    badgeVerifiedOperator: "Operador verificado",
    tabAll: "Todo",
    tabIntro: "Intro",
    tabBestFor: "Ideal para",
    tabWhyVisit: "Por que visitarlo",
    tabHowToGetThere: "Como llegar",
    tabPrepared: "Prepararse",
    tabEssentials: "Esenciales",
    tabSeasonal: "Temporada",
    tabEvents: "Eventos",
    tabActivities: "Actividades",
    tabWhatToDo: "Que hacer",
    tabFoodScene: "Escena gastronomica",
    tabSustainability: "Sostenibilidad",
    tabGettingAround: "Moverse",
    tabPractical: "Practico",
    tabSafety: "Seguridad",
    tabPosters: "Posters",
    tabMistakes: "Errores",
    tabItinerary: "Itinerario",
    tabFaq: "FAQ",
    tabPlaces: "Lugares",
    crowdPatterns: "Nivel de afluencia",
    weatherToExpect: "Tiempo esperado",
    events: "Eventos",
    foodScene: "Escena gastronomica",
    dishesToTry: "Platos que probar",
    drinksToTry: "Vinos, cervezas y destilados que probar",
    whereToTryThem: "Donde probarlos",
    sustainability: "Sostenibilidad",
    travelLighterHere: "Viaja mas ligero aqui",
    betterLocalChoices: "Mejores elecciones locales",
    whereToDoIt: "Donde hacerlo",
    notes: "Notas",
    gettingAround: "Moverse",
    practicalInformation: "Informacion practica",
    healthAndSafety: "Salud y seguridad",
    healthSafetyDisclaimer: "Solo orientacion general. Consulta siempre los avisos oficiales de salud y seguridad para tu situacion.",
    commonMistakesToAvoid: "Errores comunes que evitar",
    mistake: "Error",
    instead: "En su lugar",
    sources: "Fuentes",
    source: "Fuente",
    localSource: "Fuente local",
    showRoute: "Mostrar ruta",
    hideRoute: "Ocultar ruta",
    showOnMaps: "Ver en Maps",
    bookingLink: "Enlace de reserva",
    nearest: "Mas cercano",
    fromPrefix: "Desde",
    openMap: "Abrir mapa",
    link: "Enlace",
    mapFood: "Mapa gastronomico",
    mapSustainability: "Mapa de sostenibilidad",
    modePlane: "Avion",
    modeTrain: "Tren",
    modeCar: "Coche",
    modeFerry: "Ferri",
    modeBus: "Bus",
    modeOther: "Otro",
    eventFestival: "Festival",
    eventConcert: "Concierto",
    eventFood: "Gastronomia",
    eventSports: "Deporte",
    eventMarket: "Mercado",
    eventCulture: "Cultura",
    eventFamily: "Familia",
    eventSeasonal: "Estacional",
    eventDefault: "Evento",
    sustainabilityTransport: "Transporte",
    sustainabilityEv: "EV y alquileres",
    sustainabilityFood: "Gastronomia",
    sustainabilityWater: "Agua",
    sustainabilityWaste: "Residuos",
    sustainabilityNature: "Naturaleza",
    sustainabilityClimate: "Clima",
    sustainabilityStay: "Alojamiento",
    healthTypeEmergency: "Emergencia",
    healthTypeHealthcare: "Salud",
    healthTypeNature: "Naturaleza",
    healthTypeWeather: "Tiempo",
    healthTypeWater: "Agua",
    healthTypeCrime: "Seguridad",
    healthTypeTransport: "Transporte",
    healthTypeVaccines: "Vacunas",
    healthTypeOther: "Otro",
    rain: "Lluvia",
    sun: "Sol",
    continueLabel: "Continuar",
    continueGuideTitle: "Continua esta guia en teravia",
    continueGuideBody: "Rellenaremos el destino. Tu le das forma segun tus preferencias.",
    saveAsIs: "Guardar tal cual",
    makeItYours: "Hazla tuya",
    sharedToast: "Compartido ✓",
    linkCopiedToast: "Enlace copiado ✓",
    couldntShareToast: "No se pudo compartir el enlace",
    foodPosterAlt: "Poster de comida",
    getPreparedTitle: "Prepararse",
    getPreparedSubtitle: "Antes de ir - una capa cultural y practica rapida.",
    getPreparedEmpty: "Anade contenido para esta subseccion (o usa Enhance).",
    preparedClothes: "Ropa",
    preparedManners: "Modales",
    preparedVocabulary: "Vocabulario",
    preparedBooks: "Libros",
    preparedMusic: "Musica",
    preparedCinema: "Cine",
    preparedCategory: "Categoria",
    preparedBring: "Llevar",
    preparedSituation: "Situacion",
    guideNotFoundTitle: "Guia no encontrada · teravia",
    guideNotFoundDescription: "Esta guia ya no esta disponible.",
  },
  pt: {
    locale: "pt-PT",
    guide: "Guia",
    trip: "Viagem",
    guides: "Guias",
    travelGuide: "Guia de viagem",
    loadingTrip: "A carregar viagem…",
    save: "Guardar",
    saved: "Guardado",
    saveGuideToProfile: "Guardar este guia no teu perfil",
    removeSavedTrips: "Remover das tuas viagens guardadas",
    share: "Partilhar",
    shareGuide: "Partilhar guia",
    shareThisGuide: "Partilhar este guia",
    back: "Voltar",
    edit: "Editar",
    draft: "Rascunho",
    handcrafted: "Artesanal",
    aiEnhanced: "Melhorado por IA",
    curatedBy: "Curado por",
    generatedBy: "Gerado por",
    draftNotice: "Rascunho - publica-o quando estiveres pronto para o tornar publico.",
    guideSections: "Secoes do guia",
    emailGuide: "Enviar este guia por email",
    watch: "Ver",
    openInstagram: "Abrir Instagram",
    openTikTok: "Abrir TikTok",
    openYouTube: "Abrir YouTube",
    openThreads: "Abrir Threads",
    teraviaCurator: "Curador Teravia",
    badgeCurator: "Curador",
    badgePioneer: "Pioneiro",
    badgeVerifiedOperator: "Operador verificado",
    tabAll: "Tudo",
    tabIntro: "Intro",
    tabBestFor: "Melhor para",
    tabWhyVisit: "Porque visitar",
    tabHowToGetThere: "Como chegar",
    tabPrepared: "Preparar",
    tabEssentials: "Essenciais",
    tabSeasonal: "Sazonal",
    tabEvents: "Eventos",
    tabActivities: "Atividades",
    tabWhatToDo: "O que fazer",
    tabFoodScene: "Cena gastronomica",
    tabSustainability: "Sustentabilidade",
    tabGettingAround: "Deslocacao",
    tabPractical: "Pratico",
    tabSafety: "Seguranca",
    tabPosters: "Posters",
    tabMistakes: "Erros",
    tabItinerary: "Itinerario",
    tabFaq: "FAQ",
    tabPlaces: "Locais",
    crowdPatterns: "Nivel de afluencia",
    weatherToExpect: "Tempo esperado",
    events: "Eventos",
    foodScene: "Cena gastronomica",
    dishesToTry: "Pratos a provar",
    drinksToTry: "Vinhos, cervejas e espirituosos a provar",
    whereToTryThem: "Onde os provar",
    sustainability: "Sustentabilidade",
    travelLighterHere: "Viaja de forma mais leve aqui",
    betterLocalChoices: "Melhores escolhas locais",
    whereToDoIt: "Onde o fazer",
    notes: "Notas",
    gettingAround: "Deslocacao",
    practicalInformation: "Informacao pratica",
    healthAndSafety: "Saude e seguranca",
    healthSafetyDisclaimer: "Apenas orientacao geral. Verifica sempre os avisos oficiais de saude e seguranca para a tua situacao.",
    commonMistakesToAvoid: "Erros comuns a evitar",
    mistake: "Erro",
    instead: "Em vez disso",
    sources: "Fontes",
    source: "Fonte",
    localSource: "Fonte local",
    showRoute: "Mostrar rota",
    hideRoute: "Ocultar rota",
    showOnMaps: "Ver no Maps",
    bookingLink: "Link de reserva",
    nearest: "Mais proximo",
    fromPrefix: "De",
    openMap: "Abrir mapa",
    link: "Link",
    mapFood: "Mapa gastronomico",
    mapSustainability: "Mapa de sustentabilidade",
    modePlane: "Aviao",
    modeTrain: "Comboio",
    modeCar: "Carro",
    modeFerry: "Ferry",
    modeBus: "Autocarro",
    modeOther: "Outro",
    eventFestival: "Festival",
    eventConcert: "Concerto",
    eventFood: "Gastronomia",
    eventSports: "Desporto",
    eventMarket: "Mercado",
    eventCulture: "Cultura",
    eventFamily: "Familia",
    eventSeasonal: "Sazonal",
    eventDefault: "Evento",
    sustainabilityTransport: "Transporte",
    sustainabilityEv: "VE e alugueres",
    sustainabilityFood: "Gastronomia",
    sustainabilityWater: "Agua",
    sustainabilityWaste: "Residuos",
    sustainabilityNature: "Natureza",
    sustainabilityClimate: "Clima",
    sustainabilityStay: "Estadia",
    healthTypeEmergency: "Emergencia",
    healthTypeHealthcare: "Saude",
    healthTypeNature: "Natureza",
    healthTypeWeather: "Tempo",
    healthTypeWater: "Agua",
    healthTypeCrime: "Seguranca",
    healthTypeTransport: "Transporte",
    healthTypeVaccines: "Vacinas",
    healthTypeOther: "Outro",
    rain: "Chuva",
    sun: "Sol",
    continueLabel: "Continuar",
    continueGuideTitle: "Continua este guia na teravia",
    continueGuideBody: "Preenchemos o destino. Tu moldas tudo as tuas preferencias.",
    saveAsIs: "Guardar como esta",
    makeItYours: "Torna-o teu",
    sharedToast: "Partilhado ✓",
    linkCopiedToast: "Link copiado ✓",
    couldntShareToast: "Nao foi possivel partilhar o link",
    foodPosterAlt: "Poster gastronomico",
    getPreparedTitle: "Prepara-te",
    getPreparedSubtitle: "Antes de ires - uma camada cultural e pratica rapida.",
    getPreparedEmpty: "Adiciona conteudo a esta subsecao (ou usa Enhance).",
    preparedClothes: "Roupa",
    preparedManners: "Boas maneiras",
    preparedVocabulary: "Vocabulario",
    preparedBooks: "Livros",
    preparedMusic: "Musica",
    preparedCinema: "Cinema",
    preparedCategory: "Categoria",
    preparedBring: "Levar",
    preparedSituation: "Situacao",
    guideNotFoundTitle: "Guia nao encontrado · teravia",
    guideNotFoundDescription: "Este guia ja nao esta disponivel.",
  },
  nl: {
    locale: "nl-NL",
    guide: "Gids",
    trip: "Reis",
    guides: "Gidsen",
    travelGuide: "Reisgids",
    loadingTrip: "Reis laden…",
    save: "Opslaan",
    saved: "Opgeslagen",
    saveGuideToProfile: "Sla deze gids op in je profiel",
    removeSavedTrips: "Verwijderen uit je opgeslagen reizen",
    share: "Delen",
    shareGuide: "Gids delen",
    shareThisGuide: "Deze gids delen",
    back: "Terug",
    edit: "Bewerken",
    draft: "Concept",
    handcrafted: "Handgemaakt",
    aiEnhanced: "Verbeterd met AI",
    curatedBy: "Gecureerd door",
    generatedBy: "Gegenereerd door",
    draftNotice: "Concept - publiceer het wanneer je klaar bent om het openbaar te maken.",
    guideSections: "Gidssecties",
    emailGuide: "Deze gids mailen",
    watch: "Kijken",
    openInstagram: "Instagram openen",
    openTikTok: "TikTok openen",
    openYouTube: "YouTube openen",
    openThreads: "Threads openen",
    teraviaCurator: "Teravia-curator",
    badgeCurator: "Curator",
    badgePioneer: "Pionier",
    badgeVerifiedOperator: "Geverifieerde operator",
    tabAll: "Alles",
    tabIntro: "Intro",
    tabBestFor: "Beste voor",
    tabWhyVisit: "Waarom bezoeken",
    tabHowToGetThere: "Hoe kom je er",
    tabPrepared: "Voorbereiden",
    tabEssentials: "Essentials",
    tabSeasonal: "Seizoen",
    tabEvents: "Evenementen",
    tabActivities: "Activiteiten",
    tabWhatToDo: "Wat te doen",
    tabFoodScene: "Eten en drinken",
    tabSustainability: "Duurzaamheid",
    tabGettingAround: "Vervoer ter plekke",
    tabPractical: "Praktisch",
    tabSafety: "Veiligheid",
    tabPosters: "Posters",
    tabMistakes: "Fouten",
    tabItinerary: "Route",
    tabFaq: "FAQ",
    tabPlaces: "Plekken",
    crowdPatterns: "Druktepatroon",
    weatherToExpect: "Verwacht weer",
    events: "Evenementen",
    foodScene: "Eten en drinken",
    dishesToTry: "Gerechten om te proberen",
    drinksToTry: "Wijnen, bieren en sterke drank om te proberen",
    whereToTryThem: "Waar je ze kunt proberen",
    sustainability: "Duurzaamheid",
    travelLighterHere: "Reis hier lichter",
    betterLocalChoices: "Betere lokale keuzes",
    whereToDoIt: "Waar je het kunt doen",
    notes: "Notities",
    gettingAround: "Vervoer ter plekke",
    practicalInformation: "Praktische informatie",
    healthAndSafety: "Gezondheid en veiligheid",
    healthSafetyDisclaimer: "Alleen algemene richtlijnen. Controleer altijd de officiele gezondheids- en veiligheidsadviezen voor jouw situatie.",
    commonMistakesToAvoid: "Veelgemaakte fouten om te vermijden",
    mistake: "Fout",
    instead: "In plaats daarvan",
    sources: "Bronnen",
    source: "Bron",
    localSource: "Lokale bron",
    showRoute: "Route tonen",
    hideRoute: "Route verbergen",
    showOnMaps: "Toon op Maps",
    bookingLink: "Boekingslink",
    nearest: "Dichtstbij",
    fromPrefix: "Vanaf",
    openMap: "Kaart openen",
    link: "Link",
    mapFood: "Eten-kaart",
    mapSustainability: "Duurzaamheidskaart",
    modePlane: "Vliegtuig",
    modeTrain: "Trein",
    modeCar: "Auto",
    modeFerry: "Veerboot",
    modeBus: "Bus",
    modeOther: "Overig",
    eventFestival: "Festival",
    eventConcert: "Concert",
    eventFood: "Eten",
    eventSports: "Sport",
    eventMarket: "Markt",
    eventCulture: "Cultuur",
    eventFamily: "Familie",
    eventSeasonal: "Seizoensgebonden",
    eventDefault: "Evenement",
    sustainabilityTransport: "Vervoer",
    sustainabilityEv: "EV en verhuur",
    sustainabilityFood: "Eten",
    sustainabilityWater: "Water",
    sustainabilityWaste: "Afval",
    sustainabilityNature: "Natuur",
    sustainabilityClimate: "Klimaat",
    sustainabilityStay: "Verblijf",
    healthTypeEmergency: "Noodgeval",
    healthTypeHealthcare: "Zorg",
    healthTypeNature: "Natuur",
    healthTypeWeather: "Weer",
    healthTypeWater: "Water",
    healthTypeCrime: "Veiligheid",
    healthTypeTransport: "Vervoer",
    healthTypeVaccines: "Vaccins",
    healthTypeOther: "Overig",
    rain: "Regen",
    sun: "Zon",
    continueLabel: "Verder",
    continueGuideTitle: "Ga verder met deze gids in teravia",
    continueGuideBody: "Wij vullen de bestemming alvast in. Jij maakt hem passend voor je voorkeuren.",
    saveAsIs: "Zo opslaan",
    makeItYours: "Maak hem van jou",
    sharedToast: "Gedeeld ✓",
    linkCopiedToast: "Link gekopieerd ✓",
    couldntShareToast: "Kon de link niet delen",
    foodPosterAlt: "Food-poster",
    getPreparedTitle: "Goed voorbereid",
    getPreparedSubtitle: "Voor je gaat - een snelle culturele en praktische laag.",
    getPreparedEmpty: "Voeg inhoud toe aan deze subsectie (of gebruik Enhance).",
    preparedClothes: "Kleding",
    preparedManners: "Manieren",
    preparedVocabulary: "Woordenschat",
    preparedBooks: "Boeken",
    preparedMusic: "Muziek",
    preparedCinema: "Cinema",
    preparedCategory: "Categorie",
    preparedBring: "Meenemen",
    preparedSituation: "Situatie",
    guideNotFoundTitle: "Gids niet gevonden · teravia",
    guideNotFoundDescription: "Deze gids is niet langer beschikbaar.",
  },
  sv: {
    locale: "sv-SE",
    guide: "Guide",
    trip: "Resa",
    guides: "Guider",
    travelGuide: "Reseguide",
    loadingTrip: "Laddar resa…",
    save: "Spara",
    saved: "Sparad",
    saveGuideToProfile: "Spara den har guiden i din profil",
    removeSavedTrips: "Ta bort fran dina sparade resor",
    share: "Dela",
    shareGuide: "Dela guide",
    shareThisGuide: "Dela den har guiden",
    back: "Tillbaka",
    edit: "Redigera",
    draft: "Utkast",
    handcrafted: "Handgjord",
    aiEnhanced: "AI-forstarkt",
    curatedBy: "Kurerad av",
    generatedBy: "Genererad av",
    draftNotice: "Utkast - publicera nar du ar redo att gora den offentlig.",
    guideSections: "Guidesektioner",
    emailGuide: "Maila den har guiden",
    watch: "Se",
    openInstagram: "Oppna Instagram",
    openTikTok: "Oppna TikTok",
    openYouTube: "Oppna YouTube",
    openThreads: "Oppna Threads",
    teraviaCurator: "Teravia-kurator",
    badgeCurator: "Kurator",
    badgePioneer: "Pionjar",
    badgeVerifiedOperator: "Verifierad operator",
    tabAll: "Alla",
    tabIntro: "Intro",
    tabBestFor: "Bast for",
    tabWhyVisit: "Varfor besoka",
    tabHowToGetThere: "Hur du tar dig dit",
    tabPrepared: "Forbered",
    tabEssentials: "Det viktigaste",
    tabSeasonal: "Sasong",
    tabEvents: "Evenemang",
    tabActivities: "Aktiviteter",
    tabWhatToDo: "Vad man kan gora",
    tabFoodScene: "Matscen",
    tabSustainability: "Hallbarhet",
    tabGettingAround: "Ta sig runt",
    tabPractical: "Praktiskt",
    tabSafety: "Sakerhet",
    tabPosters: "Posters",
    tabMistakes: "Misstag",
    tabItinerary: "Resplan",
    tabFaq: "FAQ",
    tabPlaces: "Platser",
    crowdPatterns: "Tryck och folkmangd",
    weatherToExpect: "Forvantat vader",
    events: "Evenemang",
    foodScene: "Matscen",
    dishesToTry: "Ratter att prova",
    drinksToTry: "Viner, ol och sprit att prova",
    whereToTryThem: "Var du provar dem",
    sustainability: "Hallbarhet",
    travelLighterHere: "Res lattare har",
    betterLocalChoices: "Battre lokala val",
    whereToDoIt: "Var du gor det",
    notes: "Noter",
    gettingAround: "Ta sig runt",
    practicalInformation: "Praktisk information",
    healthAndSafety: "Halsa och sakerhet",
    healthSafetyDisclaimer: "Endast allman vagledning. Kontrollera alltid officiella halso- och sakerhetsrad for din situation.",
    commonMistakesToAvoid: "Vanliga misstag att undvika",
    mistake: "Misstag",
    instead: "Istallet",
    sources: "Kallor",
    source: "Kalla",
    localSource: "Lokal kalla",
    showRoute: "Visa rutt",
    hideRoute: "Dolj rutt",
    showOnMaps: "Visa pa Maps",
    bookingLink: "Bokningslank",
    nearest: "Narmast",
    fromPrefix: "Fran",
    openMap: "Oppna karta",
    link: "Lank",
    mapFood: "Matkarta",
    mapSustainability: "Hallbarhetskarta",
    modePlane: "Flyg",
    modeTrain: "Tag",
    modeCar: "Bil",
    modeFerry: "Farja",
    modeBus: "Buss",
    modeOther: "Annat",
    eventFestival: "Festival",
    eventConcert: "Konsert",
    eventFood: "Mat",
    eventSports: "Sport",
    eventMarket: "Marknad",
    eventCulture: "Kultur",
    eventFamily: "Familj",
    eventSeasonal: "Sasong",
    eventDefault: "Evenemang",
    sustainabilityTransport: "Transport",
    sustainabilityEv: "Elbilar och hyror",
    sustainabilityFood: "Mat",
    sustainabilityWater: "Vatten",
    sustainabilityWaste: "Avfall",
    sustainabilityNature: "Natur",
    sustainabilityClimate: "Klimat",
    sustainabilityStay: "Boende",
    healthTypeEmergency: "Nodlage",
    healthTypeHealthcare: "Vard",
    healthTypeNature: "Natur",
    healthTypeWeather: "Vader",
    healthTypeWater: "Vatten",
    healthTypeCrime: "Sakerhet",
    healthTypeTransport: "Transport",
    healthTypeVaccines: "Vacciner",
    healthTypeOther: "Annat",
    rain: "Regn",
    sun: "Sol",
    continueLabel: "Fortsatt",
    continueGuideTitle: "Fortsatt den har guiden i teravia",
    continueGuideBody: "Vi fyller i destinationen i forvag. Du formar den efter dina preferenser.",
    saveAsIs: "Spara som den ar",
    makeItYours: "Gor den till din",
    sharedToast: "Delad ✓",
    linkCopiedToast: "Lank kopierad ✓",
    couldntShareToast: "Kunde inte dela lanken",
    foodPosterAlt: "Matposter",
    getPreparedTitle: "Forbered dig",
    getPreparedSubtitle: "Innan du aker - ett snabbt kulturellt och praktiskt lager.",
    getPreparedEmpty: "Lagg till innehall for den har undersektionen (eller anvand Enhance).",
    preparedClothes: "Klader",
    preparedManners: "Etikett",
    preparedVocabulary: "Ordforrad",
    preparedBooks: "Bocker",
    preparedMusic: "Musik",
    preparedCinema: "Film",
    preparedCategory: "Kategori",
    preparedBring: "Ta med",
    preparedSituation: "Situation",
    guideNotFoundTitle: "Guide hittades inte · teravia",
    guideNotFoundDescription: "Den har guiden ar inte langre tillganglig.",
  },
  ja: {
    locale: "ja-JP",
    guide: "ガイド",
    trip: "旅行",
    guides: "ガイド",
    travelGuide: "旅行ガイド",
    loadingTrip: "旅行を読み込み中…",
    save: "保存",
    saved: "保存済み",
    saveGuideToProfile: "このガイドをプロフィールに保存",
    removeSavedTrips: "保存済みの旅行から削除",
    share: "共有",
    shareGuide: "ガイドを共有",
    shareThisGuide: "このガイドを共有",
    back: "戻る",
    edit: "編集",
    draft: "下書き",
    handcrafted: "ハンドクラフト",
    aiEnhanced: "AI強化",
    curatedBy: "キュレーション",
    generatedBy: "生成者",
    draftNotice: "下書きです。公開できる状態になったら公開してください。",
    guideSections: "ガイドのセクション",
    emailGuide: "このガイドをメールで送る",
    watch: "見る",
    openInstagram: "Instagramを開く",
    openTikTok: "TikTokを開く",
    openYouTube: "YouTubeを開く",
    openThreads: "Threadsを開く",
    teraviaCurator: "Teraviaキュレーター",
    badgeCurator: "キュレーター",
    badgePioneer: "パイオニア",
    badgeVerifiedOperator: "認証済みオペレーター",
    tabAll: "すべて",
    tabIntro: "概要",
    tabBestFor: "おすすめ",
    tabWhyVisit: "訪れる理由",
    tabHowToGetThere: "行き方",
    tabPrepared: "準備",
    tabEssentials: "必須情報",
    tabSeasonal: "季節",
    tabEvents: "イベント",
    tabActivities: "アクティビティ",
    tabWhatToDo: "やること",
    tabFoodScene: "食のシーン",
    tabSustainability: "サステナビリティ",
    tabGettingAround: "移動",
    tabPractical: "実用情報",
    tabSafety: "安全",
    tabPosters: "ポスター",
    tabMistakes: "注意点",
    tabItinerary: "旅程",
    tabFaq: "FAQ",
    tabPlaces: "場所",
    crowdPatterns: "混雑の傾向",
    weatherToExpect: "期待できる天気",
    events: "イベント",
    foodScene: "食のシーン",
    dishesToTry: "試したい料理",
    drinksToTry: "試したいワイン・ビール・スピリッツ",
    whereToTryThem: "どこで試すか",
    sustainability: "サステナビリティ",
    travelLighterHere: "ここでより軽やかに旅する",
    betterLocalChoices: "より良いローカルな選択",
    whereToDoIt: "どこでできるか",
    notes: "メモ",
    gettingAround: "移動",
    practicalInformation: "実用情報",
    healthAndSafety: "健康と安全",
    healthSafetyDisclaimer: "一般的な案内のみです。必ずあなたの状況に応じた公式の健康・安全情報を確認してください。",
    commonMistakesToAvoid: "避けたいよくある失敗",
    mistake: "失敗",
    instead: "代わりに",
    sources: "情報源",
    source: "情報源",
    localSource: "ローカル情報源",
    showRoute: "ルートを表示",
    hideRoute: "ルートを隠す",
    showOnMaps: "Mapsで見る",
    bookingLink: "予約リンク",
    nearest: "最寄り",
    fromPrefix: "出発",
    openMap: "地図を開く",
    link: "リンク",
    mapFood: "フードマップ",
    mapSustainability: "サステナビリティマップ",
    modePlane: "飛行機",
    modeTrain: "電車",
    modeCar: "車",
    modeFerry: "フェリー",
    modeBus: "バス",
    modeOther: "その他",
    eventFestival: "フェスティバル",
    eventConcert: "コンサート",
    eventFood: "食",
    eventSports: "スポーツ",
    eventMarket: "マーケット",
    eventCulture: "文化",
    eventFamily: "家族",
    eventSeasonal: "季節",
    eventDefault: "イベント",
    sustainabilityTransport: "交通",
    sustainabilityEv: "EVとレンタル",
    sustainabilityFood: "食",
    sustainabilityWater: "水",
    sustainabilityWaste: "廃棄物",
    sustainabilityNature: "自然",
    sustainabilityClimate: "気候",
    sustainabilityStay: "滞在",
    healthTypeEmergency: "緊急",
    healthTypeHealthcare: "医療",
    healthTypeNature: "自然",
    healthTypeWeather: "天気",
    healthTypeWater: "水",
    healthTypeCrime: "防犯",
    healthTypeTransport: "交通",
    healthTypeVaccines: "予防接種",
    healthTypeOther: "その他",
    rain: "雨",
    sun: "日照",
    continueLabel: "続ける",
    continueGuideTitle: "このガイドをteraviaで続ける",
    continueGuideBody: "行き先はこちらで入力します。あとはあなたの好みに合わせて整えてください。",
    saveAsIs: "このまま保存",
    makeItYours: "自分用にする",
    sharedToast: "共有しました ✓",
    linkCopiedToast: "リンクをコピーしました ✓",
    couldntShareToast: "リンクを共有できませんでした",
    foodPosterAlt: "フードポスター",
    getPreparedTitle: "準備する",
    getPreparedSubtitle: "出発前に - 文化的・実用的な要点をすばやく確認。",
    getPreparedEmpty: "このサブセクションに内容を追加してください（または Enhance を使用）。",
    preparedClothes: "服装",
    preparedManners: "マナー",
    preparedVocabulary: "語彙",
    preparedBooks: "本",
    preparedMusic: "音楽",
    preparedCinema: "映画",
    preparedCategory: "カテゴリー",
    preparedBring: "持ち物",
    preparedSituation: "場面",
    guideNotFoundTitle: "ガイドが見つかりません · teravia",
    guideNotFoundDescription: "このガイドは利用できなくなりました。",
  },
};

export function getGuideUiStrings(code?: GuideLanguageCode | GuideTranslatedLanguageCode | string | null): GuideUiStrings {
  const normalized = String(code || "original").trim().toLowerCase();
  if (!normalized || normalized === "original" || normalized === "en") return ENGLISH_UI;
  const translated = GUIDE_UI_BY_LANG[normalized as GuideTranslatedLanguageCode];
  if (!translated) return ENGLISH_UI;
  return { ...ENGLISH_UI, ...translated };
}
